import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { adminAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiImage } from 'react-icons/fi';
import { formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './Manage.css';

const ManageCertifications = () => {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', issuer: '', issueDate: '', expiryDate: '', credentialUrl: '', description: '' });

  const loadData = async () => {
    try {
      const res = await adminAPI.getCertifications();
      if (res.success) setCerts(res.data);
    } catch (err) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', issuer: '', issueDate: '', expiryDate: '', credentialUrl: '', description: '' });
    setShowModal(true);
  };

  const openEdit = (cert) => {
    setEditing(cert);
    setForm({
      name: cert.name, issuer: cert.issuer,
      issueDate: cert.issueDate?.split('T')[0] || '', expiryDate: cert.expiryDate?.split('T')[0] || '',
      credentialUrl: cert.credentialUrl || '', description: cert.description || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await adminAPI.updateCertification(editing.id, form); toast.success('Updated'); }
      else { await adminAPI.createCertification(form); toast.success('Created'); }
      setShowModal(false); loadData();
    } catch (err) { toast.error('Operation failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this certification?')) return;
    try { await adminAPI.deleteCertification(id); toast.success('Deleted'); loadData(); }
    catch (err) { toast.error('Failed to delete'); }
  };

  const handleImageUpload = async (certId, file) => {
    try { await adminAPI.uploadCertImage(certId, file); toast.success('Image uploaded'); loadData(); }
    catch (err) { toast.error('Upload failed'); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="manage-page">
      <Helmet><title>Manage Certifications | Portfolio CMS</title></Helmet>
      <div className="manage-header">
        <h2>Certifications</h2>
        <button className="btn btn-primary" onClick={openCreate}><FiPlus /> New Certification</button>
      </div>
      <div className="manage-table">
        <table>
          <thead><tr><th>Name</th><th>Issuer</th><th>Issue Date</th><th>Image</th><th>Actions</th></tr></thead>
          <tbody>
            {certs.map(c => (
              <tr key={c.id}>
                <td><strong>{c.name}</strong></td>
                <td>{c.issuer}</td>
                <td>{formatDate(c.issueDate)}</td>
                <td>
                  <label className="upload-btn-sm">
                    <FiImage />
                    <input type="file" hidden accept="image/*" onChange={e => e.target.files[0] && handleImageUpload(c.id, e.target.files[0])} />
                  </label>
                </td>
                <td><div className="action-btns">
                  <button className="btn-icon edit" onClick={() => openEdit(c)}><FiEdit2 /></button>
                  <button className="btn-icon delete" onClick={() => handleDelete(c.id)}><FiTrash2 /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editing ? 'Edit Certification' : 'New Certification'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group"><label>Name</label><input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required /></div>
                <div className="form-group"><label>Issuer</label><input value={form.issuer} onChange={e => setForm({...form, issuer: e.target.value})} required /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Issue Date</label><input type="date" value={form.issueDate} onChange={e => setForm({...form, issueDate: e.target.value})} required /></div>
                <div className="form-group"><label>Expiry Date</label><input type="date" value={form.expiryDate} onChange={e => setForm({...form, expiryDate: e.target.value})} /></div>
              </div>
              <div className="form-group"><label>Credential URL</label><input value={form.credentialUrl} onChange={e => setForm({...form, credentialUrl: e.target.value})} /></div>
              <div className="form-group"><label>Description</label><textarea rows="3" value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCertifications;
